/* eslint-disable @typescript-eslint/no-shadow */
import { createFunction, getCustomTag, processCondition, useTranslation } from '@msdk/common'
import {
  Control,
  EntityModel,
  FieldType,
  FilterCollection,
  FilterDefinition,
  FormNode,
  GenericFieldValue,
  LogicalPath,
  UserPath,
  ViewAcl,
  ViewFilterAlignment,
} from '@msdk/definitions'
import { ButtonIcon, ButtonPrimary, COLORS, IconSelector, styled, Typography } from '@msdk/ui'
import React, { useEffect, useState } from 'react'

import { ChangeArgs } from '../../models/form'
import { ConditionalFieldRenderer } from '../Fields'
import { BinnedItems } from './BinnedItems'

const FilterContainer = styled('div')`
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
  gap: 8px;
  flex: 1;
`

const RightFiltersContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
`

const FiltersClearContainer = styled('div')`
  display: flex;
  align-items: start;
  justify-content: flex-start;
  width: 100%;
  gap: 16px;
  flex: 1;
`

const StyledButtonIcon = styled(ButtonIcon)`
  display: flex;
  align-items: end;
`

interface Props {
  filters: FilterCollection
  prefilledFilters?: Record<string, GenericFieldValue>
  onFilterChange: (filter: Record<string, GenericFieldValue>) => void
  multiselect: boolean
  setMultiselect: (value: boolean) => void
  acl: ViewAcl
  entityModel: EntityModel
  viewName: string
  hideBinnedItemsFilter?: boolean
  initialValues?: Record<string, GenericFieldValue>
}

export function Filters({
  filters,
  onFilterChange,
  multiselect,
  setMultiselect,
  prefilledFilters,
  acl,
  entityModel,
  viewName,
  hideBinnedItemsFilter = false,
  initialValues,
}: Props) {
  const t = useTranslation()

  const [entity, setEntity] = useState<Record<string, GenericFieldValue>>({})
  const [nodes, setNodes] = useState<(FormNode & { size: number })[]>([])

  const evaluateExpression = (exp: string): string => {
    const func = createFunction(processCondition(exp))
    return func({ entity: undefined, row: undefined, logic: new LogicalPath(), user: new UserPath() })
  }

  const getFieldType = (f: FilterDefinition) => {
    if (f.customUI) return 'Custom'
    return f.inlineDefinition.fieldDefinitionType
  }

  useEffect(() => {
    const newEntity: Record<string, GenericFieldValue> = {}
    const n = filters.map((f) => {
      let v = null

      if (f.defaultValue) {
        v = f.inlineDefinition.getFieldValue()
        v.value = evaluateExpression(f.defaultValue)
      }

      if (prefilledFilters && prefilledFilters[f.parameterKey]) {
        v = prefilledFilters[f.parameterKey]
      }

      if (initialValues && initialValues[f.parameterKey]) {
        v = initialValues[f.parameterKey]
        newEntity[f.parameterKey] = v
      }

      const ctrl = {
        id: f.inlineDefinition.name,
        value: {
          data: v ?? null,
          fieldDefinition: f.inlineDefinition,
          formDefinition: {
            type: 'Field',
            customUI: f.customUI,
            alignment: f.alignment,
            entityName: f.inlineDefinition.entityName,
            formName: viewName,
          } as any,
          isDirty: false,
          state: f.readOnly ? 'Readonly' : 'Enabled',
          visibility: f.visible ? 'Visible' : 'Invisible',
          type: getFieldType(f),
          validationMessages: [],
          validationParams: [],
          validationState: 'Valid',
          validationFlags: 0,
          entityName: f.inlineDefinition.entityName,
        } as Control,
      }

      const size =
        f.inlineDefinition.idml && f.inlineDefinition.description
          ? t(f.inlineDefinition.idml, f.inlineDefinition.description).length
          : 0

      return { ...ctrl, size }
    })
    setEntity(newEntity)
    setNodes(n)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, initialValues])

  const onChange = (args: ChangeArgs) => {
    const filter = filters.find((f) => f.inlineDefinition.name === args.fieldName) as FilterDefinition
    const newEntity = { ...entity }
    newEntity[filter.parameterKey] = args.value
    /**
     * special case for entity status, when setting to active whe are turning off the toggle
     * graphically it looks like whe are removing the filter, I am removing the actual filter
     * to allow the logic that checks if there is some filters to work properly
     */
    if (filter.parameterKey === '@ENTITYSTATUS' && args.value?.value === 'ACTIVE') {
      delete newEntity[filter.parameterKey]
    }
    setEntity(newEntity)
    const node = nodes.find((n) => n.id === args.fieldName) as FormNode
    node.value.data = args.value
    setNodes([...nodes])
    if (filter.applyOnChange) {
      onFilterChange(newEntity)
    }
  }

  const onButtonClick = () => {
    onFilterChange(entity)
  }

  const showSearchButton = filters.some((filter) => !filter.applyOnChange && filter.visible)

  const isRightAlignedFilter = (node: FormNode) => {
    const alignment = (node.value.formDefinition as any)?.alignment
    return alignment === ViewFilterAlignment.Right
  }

  const leftAlignedNodes = nodes.filter((n) => !isRightAlignedFilter(n))
  const rightAlignedNodes = nodes.filter((n) => isRightAlignedFilter(n))

  /**
   * Checks if there are any active filters with a value set, disregarding binned items and right aligned filters.
   */
  const hasActiveFilters = !!Object.entries(entity).some(
    ([k, v]) =>
      k !== '@ENTITYSTATUS' &&
      v !== null &&
      filters.find((f) => f.parameterKey === k)?.visible &&
      !rightAlignedNodes.some((r) => r.value.data === v),
  )

  /**
   * Clears all filters and resets the nodes values.
   * This will only reset inline filters. Column filters are not affected.
   * Binned items entry is preserved if it exists in the current filters.
   */
  function clearFilters() {
    const newEntity: Record<string, GenericFieldValue> = {}

    /**
     * Preserve the binned items entry if it exists in the current entity.
     */
    if (entity['@ENTITYSTATUS']) {
      newEntity['@ENTITYSTATUS'] = entity['@ENTITYSTATUS']
    }

    setEntity(newEntity)

    /**
     * Reset all nodes values to null, except for the binned items entry.
     */
    const clearedNodes = nodes.map((n) => {
      if (n.value.fieldDefinition?.name === '@ENTITYSTATUS') return n

      n.value.data = null
      return n
    })

    setNodes(clearedNodes)

    onFilterChange(newEntity)
  }

  return (
    <FilterContainer>
      {leftAlignedNodes.map((n) => {
        return (
          <React.Fragment key={n.id}>
            {!hideBinnedItemsFilter && entityModel.showBinnedItemsFilter && n.id === '@ENTITYSTATUS' && (
              <BinnedItems
                disabled={!acl.canManageBin}
                multiselect={multiselect}
                setMultiselect={setMultiselect}
                node={n}
                // eslint-disable-next-line @typescript-eslint/no-shadow
                onChange={(n) => {
                  onChange(n)
                }}
              />
            )}
            {n.id !== '@ENTITYSTATUS' && (
              <ConditionalFieldRenderer
                node={n}
                type={n.value.type as FieldType}
                onChange={(n) => {
                  onChange(n)
                }}
                filterField={true}
                size={n.size}
                showLabel={false}
                showPlaceholder={true}
                useLabelAsPlaceholder={true}
              ></ConditionalFieldRenderer>
            )}
          </React.Fragment>
        )
      })}
      {showSearchButton && (
        <ButtonPrimary onClick={() => onButtonClick()} data-test-id={getCustomTag(['view', 'search', 'button'])}>
          {t('CMPSR_SEARCH', 'Search')}
        </ButtonPrimary>
      )}
      {hasActiveFilters && (
        <FiltersClearContainer>
          <StyledButtonIcon onClick={clearFilters}>
            <IconSelector iconName="Close" color="primary" />
            <Typography variant="button" color={COLORS.primary.main}>
              {t('LBL_CLEAR_FILTERS', 'Clear')}
            </Typography>
          </StyledButtonIcon>
        </FiltersClearContainer>
      )}
      {rightAlignedNodes.length > 0 && (
        <RightFiltersContainer>
          {rightAlignedNodes.map((n) => (
            <ConditionalFieldRenderer
              key={n.id}
              node={n}
              type={n.value.type as FieldType}
              onChange={(n) => {
                onChange(n)
              }}
              filterField={true}
              size={n.size}
              showLabel={false}
              showPlaceholder={true}
              useLabelAsPlaceholder={true}
            ></ConditionalFieldRenderer>
          ))}
        </RightFiltersContainer>
      )}
    </FilterContainer>
  )
}
